import { openAuthenticatedPdf } from "@/lib/pdf";
import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/helpers";
import { SUB_STATUS, INV_STATUS, METHOD_LABEL, eur } from "@/lib/billing";
import { CreditCard, CalendarClock, Download, Settings2, FileText, ShieldCheck, Users2 } from "lucide-react";
import { BillingDetailsForm } from "@/components/BillingDetailsForm";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "@/lib/contact";

export default function Subscription() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing/me").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const sid = q.get("session_id");
    if (q.get("checkout") === "cancel") { toast("Paiement annulé — aucun changement effectué."); window.history.replaceState({}, "", "/app/abonnement"); return; }
    if (q.get("checkout") !== "success" || !sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/me/checkout/${sid}`);
        if (data.subscribed) { toast.success("Moyen de paiement enregistré — votre abonnement SanitDocs est actif."); window.history.replaceState({}, "", "/app/abonnement"); load(); return; }
      } catch (e) { /* on réessaie */ }
      if (++tries < 8) setTimeout(poll, 2000); else { toast("Confirmation en cours… actualisez dans quelques instants."); window.history.replaceState({}, "", "/app/abonnement"); load(); }
    };
    poll();
  }, []);

  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/me/manage");
      if (data.portal_url) window.location.href = data.portal_url; else toast(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Portail indisponible"); }
    finally { setBusy(false); }
  };
  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/me/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de démarrer le paiement"); setBusy(false); }
  };
  const download = (inv) => {
    if (inv.stripe_invoice_pdf || inv.stripe_hosted_invoice_url) window.open(inv.stripe_invoice_pdf || inv.stripe_hosted_invoice_url, "_blank", "noopener");
    else openAuthenticatedPdf(`/billing/me/invoices/${inv.id}/pdf`, `${inv.number}.pdf`).catch(() => toast.error("PDF indisponible"));
  };

  if (d === null) return <p className="text-slate-400">Chargement…</p>;
  if (d === false) return <p className="text-slate-500">Aucun abonnement associé à votre entreprise.</p>;
  const { subscription: s, plan, invoices, tax, trial, recommended_plan, active_users, days_to_suspension, checkout_available, portal_available } = d;
  const st = SUB_STATUS[s.status];
  const priceSuffix = tax.regime === "vat" ? "/ mois HT" : "/ mois";

  return (
    <div className="flex flex-col gap-6" data-testid="subscription-page">
      <div>
        <h1 className="text-3xl font-heading font-extrabold text-brand-900">Abonnement & facturation</h1>
        <p className="text-slate-500 mt-1">Votre formule SanitDocs, vos échéances et vos factures. Une question ou une commande d'abonnement : <a data-testid="subscription-contact-link" href={CONTACT_MAILTO} className="underline text-accent-700 font-semibold">{CONTACT_EMAIL}</a></p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 rounded-2xl bg-brand-800 brand-grid text-white p-6 shadow-lg shadow-brand-900/20 relative overflow-hidden">
          <ShieldCheck className="absolute right-4 top-4 w-24 h-24 text-white/10" />
          <p className="text-brand-100 text-sm font-semibold">Formule actuelle{s.plan_override && <span data-testid="plan-override-tag" className="ml-2 text-[10px] uppercase tracking-wide bg-white/15 px-2 py-0.5 rounded-full">Formule personnalisée</span>}</p>
          <p data-testid="sub-plan" className="text-3xl font-heading font-extrabold mt-1">{s.plan_name}</p>
          <p className="text-brand-100 text-sm mt-1">{plan?.description}</p>
          <div className="flex items-baseline gap-2 mt-4">
            <span data-testid="sub-price" className="text-4xl font-heading font-extrabold">{eur(s.monthly_amount)}</span>
            <span className="text-brand-100">{priceSuffix}</span>
          </div>
          {tax.regime !== "vat" && <p data-testid="tax-mention" className="text-xs text-brand-200 mt-1">{tax.legal_mention}</p>}
        </div>
        {trial ? (
          <div className="bg-white rounded-2xl border border-accent-200 p-5 shadow-sm" data-testid="trial-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-accent-700 flex items-center gap-1.5"><CalendarClock className="w-4 h-4" />Essai gratuit</p>
            <p data-testid="trial-days-left" className="text-2xl font-heading font-extrabold text-brand-900 mt-2">{trial.days_left} jour{trial.days_left > 1 ? "s" : ""} restant{trial.days_left > 1 ? "s" : ""}</p>
            <p className="text-xs text-slate-500 mt-1">Du {fmtDate(trial.start)} au {fmtDate(trial.end)}</p>
            {recommended_plan && <p className="text-xs text-slate-500 mt-1">Formule applicable ensuite : <b className="text-brand-800">{recommended_plan.name} — {eur(recommended_plan.monthly_amount)}{priceSuffix}</b> ({active_users} utilisateur{active_users > 1 ? "s" : ""} actif{active_users > 1 ? "s" : ""})</p>}
          </div>
        ) : (
          <Card icon={CalendarClock} label="Prochain renouvellement" value={s.status === "cancelled" ? "—" : fmtDate(s.next_renewal_date)} testid="sub-renewal" />
        )}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Statut</p>
          <span data-testid="sub-status" className={`inline-flex mt-2 text-sm font-bold px-3 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><CreditCard className="w-4 h-4" />{METHOD_LABEL[s.payment_method] || "À définir"}</p>
        </div>
      </div>

      {s.status === "payment_pending" && (
        <div data-testid="payment-warning" className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-orange-800 font-semibold">
          Un paiement n'a pas pu être encaissé. Votre accès reste ouvert{days_to_suspension != null && <> encore <b>{Math.max(days_to_suspension, 0)} jour{days_to_suspension > 1 ? "s" : ""}</b></>} : régularisez votre facture pour éviter le passage en lecture seule.
        </div>
      )}
      {s.status === "suspended" && (
        <div data-testid="suspended-warning" className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 font-semibold">
          Votre abonnement est suspendu : l'application est en lecture seule pour vos équipes. Toutes vos données sont conservées ; le service est rétabli automatiquement dès réception du paiement.
        </div>
      )}

      {s.cancel_at_period_end && s.status !== "cancelled" && (
        <div data-testid="cancel-notice" className="bg-slate-100 border border-slate-200 rounded-xl p-4 text-slate-700 font-semibold">
          Résiliation enregistrée : votre accès reste complet jusqu'au {fmtDate(s.cancel_effective_date || s.next_renewal_date)}. Vous pouvez annuler la résiliation depuis « Gérer l'abonnement ».
        </div>
      )}
      {s.pending_plan_name && (
        <div data-testid="pending-plan-notice" className="bg-accent-50 border border-accent-200 rounded-xl p-4 text-accent-800 text-sm font-semibold">
          Formule <b>{s.pending_plan_name}</b> appliquée automatiquement à la prochaine échéance ({fmtDate(s.pending_plan_effective || s.next_renewal_date)}), sans prorata. Votre formule actuelle reste inchangée jusque-là.
        </div>
      )}
      {checkout_available && (
        <div data-testid="checkout-card" className="bg-white border border-accent-200 rounded-2xl p-5 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-brand-900">{trial ? "Enregistrez votre moyen de paiement" : "Activez le paiement en ligne"}</p>
            <p className="text-sm text-slate-500 mt-1">{trial ? `Aucun prélèvement avant la fin de votre essai (${fmtDate(trial.end)}). ` : ""}Paiement sécurisé par Stripe : SanitDocs ne stocke jamais vos données bancaires.{!d.billing_details?.complete && " Complétez d'abord vos coordonnées de facturation ci-dessous."}</p>
          </div>
          <button data-testid="start-checkout" onClick={checkout} disabled={busy || !d.billing_details?.complete} className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-brand-800 hover:bg-brand-900 text-white font-bold transition-colors disabled:opacity-60">
            <CreditCard className="w-4 h-4" /> {busy ? "Redirection…" : trial ? "Ajouter ma carte" : "Souscrire en ligne"}
          </button>
        </div>
      )}
      <BillingDetailsForm initial={d.billing_details} onSaved={load} />

      <div className="flex flex-wrap gap-3">
        {(portal_available || !d.stripe_enabled) && (
          <button data-testid="manage-subscription" onClick={manage} disabled={busy} className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-accent-600 hover:bg-accent-700 text-white font-bold transition-colors disabled:opacity-60">
            <Settings2 className="w-4 h-4" /> Gérer l'abonnement
          </button>
        )}
        <span className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-slate-100 text-slate-600 text-sm"><Users2 className="w-4 h-4" />{active_users} utilisateur{active_users > 1 ? "s" : ""} actif{active_users > 1 ? "s" : ""} · {plan?.max_users ? `jusqu'à ${plan.max_users}` : "illimités"}</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center gap-2"><FileText className="w-5 h-5 text-accent-600" /><h2 className="font-heading font-bold text-lg text-brand-900">Factures</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            <th className="px-4 py-3 font-semibold">Numéro</th><th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3 font-semibold">Période</th>
            <th className="px-4 py-3 font-semibold text-right">Total</th><th className="px-4 py-3 font-semibold">Statut</th><th className="px-4 py-3 font-semibold text-right">PDF</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((i) => { const m = INV_STATUS[i.status]; return (
              <tr key={i.id} data-testid={`my-invoice-${i.number}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-800">{i.number}{i.kind === "credit_note" && <span className="ml-2 text-xs text-slate-400">Avoir</span>}{i.source === "stripe" && <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">Stripe</span>}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(i.issue_date)}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(i.period_start)} → {fmtDate(i.period_end)}</td>
                <td className="px-4 py-3 text-right font-heading font-bold text-brand-900">{eur(i.total)}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full border ${m.cls}`}>{m.label}</span></td>
                <td className="px-4 py-3 text-right"><button data-testid={`download-${i.number}`} onClick={() => download(i)} className="inline-flex items-center gap-1 text-accent-700 font-semibold hover:underline"><Download className="w-4 h-4" />Télécharger</button></td>
              </tr>); })}
            {invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucune facture pour le moment.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</p>
      <p data-testid={testid} className="text-2xl font-heading font-extrabold text-brand-900 mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}
